import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaFileAlt, FaCheck, FaBolt, FaBullseye, FaShieldAlt } from 'react-icons/fa';
import Navbar from './Navbar';
import Footer from './Footer';
import api from '../api/axios';

const FEATURES = [
  [FaBullseye, 'Questions based on your skills and experience'],
  [FaBolt, 'Instant score, strengths and improvements'],
  [FaShieldAlt, 'Your file is not saved'],
];

export default function GuestMockInterview() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState('upload'); // 'upload' | 'interview' | 'locked'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [targetRole, setTargetRole] = useState('');
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [difficulty, setDifficulty] = useState('normal');
  const [dragging, setDragging] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setError('');
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) { setFile(null); setError('Please upload a PDF or DOCX file.'); return; }
    if (f.size > 5 * 1024 * 1024) { setFile(null); setError('File is too large. Maximum size is 5 MB.'); return; }
    setFile(f);
  };

  const handleStart = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('resume', file);
      form.append('difficulty', difficulty);
      const res = await api.post('/guest/mock-interview/start', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQuestions(res.data.questions || []);
      setTargetRole(res.data.target_role || '');
      setIndex(0); setAnswer(''); setFeedback(null);
      setStage('interview');
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitReached(true);
        setError('You have reached the free limit for now. Register to keep practising.');
      } else {
        setError(err.response?.data?.errors?.resume?.[0] || err.response?.data?.message || 'Could not start the interview. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    const current = questions[index];
    if (!current || answer.trim().length < 5) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/guest/mock-interview/feedback', {
        question: current.question, category: current.category, answer: answer.trim(), target_role: targetRole,
      });
      setFeedback(res.data);
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitReached(true);
        setError('You have reached the free limit for now. Register to keep practising.');
      } else {
        setError(err.response?.data?.message || 'Could not evaluate your answer. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleNext = () => {
    if (index + 1 < questions.length) {
      setIndex((i) => i + 1); setAnswer(''); setFeedback(null); setError('');
    } else {
      setStage('locked');
    }
  };

  const scoreColor = (s) => (s >= 7 ? 'text-[#10B981]' : s >= 5 ? 'text-[#F59E0B]' : 'text-[#EF4444]');
  const btn = 'gm-btn text-white w-full px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] disabled:opacity-50';

  return (
    <div className="min-h-screen bg-[#FBF9FE] overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .gm-grad{background-image:linear-gradient(90deg,#7C3AED,#DB2777)}
        .gm-btn{background-image:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED);background-size:200% 100%;transition:background-position .5s ease,transform .25s ease}
        .gm-btn:hover:not(:disabled){background-position:100% 0;transform:translateY(-2px)}
        @keyframes gm-rise{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
        @keyframes gm-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes gm-border{0%{background-position:0% 50%}100%{background-position:300% 50%}}
        @keyframes gm-dot{0%,60%,100%{transform:translateY(0);opacity:.35}30%{transform:translateY(-4px);opacity:1}}
        .gm-rise{animation:gm-rise .7s cubic-bezier(.2,.8,.2,1) both}
        .gm-bob{animation:gm-bob 3.5s ease-in-out infinite}
        .gm-card{position:relative;border:0}
        .gm-card::before{content:'';position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
          background:linear-gradient(120deg,#7C3AED,#DB2777,#F59E0B,#7C3AED);background-size:300% 300%;animation:gm-border 6s linear infinite;
          -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude}
        .gm-drop{border:2px dashed #D8C8F7;background:#FBF5FF;transition:border-color .25s,background .25s}
        .gm-drop:hover,.gm-drop-on{border-color:#7C3AED;background:#F5EEFF}
        .gm-drop-done{border-style:solid;border-color:#10B981;background:#ECFDF5}
        .gm-dot{width:6px;height:6px;border-radius:9999px;background:#7C3AED;display:inline-block;animation:gm-dot 1.1s ease-in-out infinite}
        .gm-dot:nth-child(2){animation-delay:.15s}.gm-dot:nth-child(3){animation-delay:.3s}
        @media (prefers-reduced-motion:reduce){.gm-rise,.gm-bob,.gm-card::before,.gm-dot{animation:none}}
      `}</style>

      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* left */}
        <div className="gm-rise">
          <p className="inline-block text-xs font-semibold text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1.5 mb-5">Free, no login needed</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight mb-4 bg-gradient-to-r from-[#5B21B6] to-[#BE185D] bg-clip-text text-transparent w-fit max-w-full">
            Practise a mock interview from your own resume
          </h1>
          <p className="text-gray-600 leading-relaxed mb-6 max-w-md">
            Upload your resume and answer 3 AI-generated questions. You get a score and feedback on every answer.
          </p>
          <ul className="space-y-3 max-w-md">
            {FEATURES.map(([I, t]) => (
              <li key={t} className="flex items-center gap-3.5 bg-white border border-purple-100 rounded-2xl px-4 py-3 shadow-sm">
                <span className="w-9 h-9 rounded-xl gm-grad text-white flex items-center justify-center shrink-0"><I /></span>
                <span className="text-sm font-semibold">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* right: card */}
        <div key={stage} className="gm-card gm-rise relative bg-white rounded-3xl p-6 sm:p-8 shadow-[0_30px_70px_-30px_rgba(124,58,237,0.45)]">
          {stage === 'locked' ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto mb-5 rounded-2xl gm-grad text-white flex items-center justify-center text-2xl"><FaShieldAlt /></div>
              <h2 className="text-xl font-bold mb-2">You have completed your 3 free questions</h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-sm mx-auto">
                Please login to continue this mock interview with more questions and feedback.
              </p>
              <button type="button" onClick={() => navigate('/login')} className={btn}>Login to continue</button>
              <p className="text-xs text-gray-500 mt-4">
                New to PlanB? <Link to="/register" className="font-semibold text-[#7C3AED] hover:underline">Register free</Link>
              </p>
            </div>
          ) : stage === 'upload' ? (
            <>
              <p className="font-semibold text-sm mb-1">Upload your resume</p>
              <p className="text-xs text-gray-500 mb-4">PDF or DOCX, up to 5 MB. Text-based files work best.</p>
              <input ref={fileInputRef} type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); handleFileChange({ target: { files: e.dataTransfer.files } }); }}
                className={`gm-drop w-full rounded-2xl p-6 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 ${dragging ? 'gm-drop-on' : ''} ${file ? 'gm-drop-done' : ''}`}
              >
                <span className="gm-bob w-12 h-12 mx-auto mb-3 rounded-2xl gm-grad text-white flex items-center justify-center text-xl">
                  {file ? <FaCheck /> : <FaFileAlt />}
                </span>
                <span className="block text-sm font-semibold break-all">{file ? file.name : 'Click to choose or drop your resume'}</span>
                <span className="block text-xs text-gray-500 mt-1">{file ? 'Ready. Click to change the file.' : 'PDF or DOCX'}</span>
              </button>

              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Difficulty</p>
                <div className="inline-flex bg-[#F5F0FA] rounded-full p-1">
                  {['easy', 'normal', 'hard'].map((k) => (
                    <button
                      key={k} type="button" onClick={() => setDifficulty(k)} disabled={loading}
                      className={`px-5 py-1.5 rounded-full text-sm font-semibold capitalize transition disabled:opacity-60 ${difficulty === k ? 'gm-grad text-white shadow-md' : 'text-gray-600 hover:text-[#7C3AED]'}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>

              {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

              {limitReached ? (
                <Link to="/register" className={`${btn} mt-5 inline-block text-center`}>Register to continue</Link>
              ) : (
                <button type="button" onClick={handleStart} disabled={!file || loading} className={`${btn} mt-5`}>
                  {loading ? 'Reading your resume...' : 'Start free mock interview'}
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-semibold text-gray-500">
                  Question {index + 1} of {questions.length}
                  <span className="text-gray-300"> · </span>{questions[index]?.category}
                </p>
                <div className="flex gap-1.5">
                  {questions.map((_, i) => <span key={i} className={`w-6 h-1.5 rounded-full ${i <= index ? 'gm-grad' : 'bg-purple-100'}`} />)}
                </div>
              </div>

              <p className="font-bold text-lg mb-4">{questions[index]?.question}</p>

              <textarea
                value={answer} onChange={(e) => setAnswer(e.target.value)} rows={5}
                disabled={!!feedback || loading}
                placeholder="Type your answer as if you are in a real interview..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40 disabled:bg-gray-50"
              />

              {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

              {!feedback ? (
                limitReached ? (
                  <Link to="/register" className={`${btn} mt-4 inline-block text-center`}>Register to continue</Link>
                ) : (
                  <button type="button" onClick={handleSubmit} disabled={loading || answer.trim().length < 5} className={`${btn} mt-4`}>
                    {loading ? 'Evaluating...' : 'Submit answer'}
                  </button>
                )
              ) : (
                <div className="mt-5 pt-5 border-t border-purple-100">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-3xl font-extrabold tabular-nums ${scoreColor(feedback.score)}`}>{feedback.score}/10</span>
                    <span className="text-xs text-gray-500">Your score for this answer</span>
                  </div>

                  {feedback.strengths?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What went well</p>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">{feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {feedback.improvements?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">How to improve</p>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">{feedback.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {feedback.sample_answer && (
                    <div className="bg-[#FBF5FF] border border-purple-100 rounded-xl p-3 mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sample better answer</p>
                      <p className="text-sm text-gray-700">{feedback.sample_answer}</p>
                    </div>
                  )}

                  <button type="button" onClick={handleNext} className={btn}>Next question</button>
                  {index + 1 >= questions.length && (
                    <p className="text-xs text-gray-500 text-center mt-3">That was your 3rd free question. Login to continue this mock interview.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}